import clsx from 'clsx'
import { Info } from 'lucide-react'
import type { MakerCapability } from '@/api/maker.ts'
import { BuilderSection, Note, SECTION_IDS } from '@/components/marketplace/maker/BuilderShell.tsx'
import type { BuilderDraft } from '@/components/marketplace/maker/builder-form.ts'
import { Badge } from '@/components/ui/primitives.tsx'
import { useI18n } from '@/i18n/index.ts'

/**
 * "What it can do" — one list.
 *
 * Whether an entry is a platform ability or something reached through a connected system is
 * a server-side distinction, and it stays there: the maker picks from a single grouped
 * checklist and the server splits the selection back into its two grants (AGM-080). Nothing
 * here is free text, so an agent cannot be granted a capability that was never curated.
 *
 * Each entry states whether it reads or acts, and the acting wording carries the promise the
 * runtime actually keeps — the approval gate is platform-owned and a maker cannot lower it.
 */
export const CapabilitiesSection = ({
  step,
  draft,
  disabled,
  capabilities,
  loading,
  failed,
  onChange
}: {
  step: number
  draft: BuilderDraft
  disabled: boolean
  capabilities: MakerCapability[]
  loading: boolean
  failed: boolean
  onChange: (patch: Partial<BuilderDraft>) => void
}) => {
  const i18n = useI18n()
  // In the body, never at module scope (PRD-27 F-3).
  const strings = i18n.t.marketplace.maker.sections.capabilities
  const granted = new Set(draft.capabilityIds)

  const toggle = (id: string, on: boolean) =>
    onChange({
      capabilityIds: on ? [...draft.capabilityIds, id] : draft.capabilityIds.filter((value) => value !== id)
    })

  // Grouped by the system the capability belongs to, in first-seen order, so the grouping
  // follows the administrator's ordering rather than an alphabetical accident.
  const groups: Array<{ system: string; items: MakerCapability[] }> = []
  for (const capability of capabilities) {
    const system = capability.system?.trim() || strings.otherSystem
    const existing = groups.find((group) => group.system === system)
    if (existing) existing.items.push(capability)
    else groups.push({ system, items: [capability] })
  }

  return (
    <BuilderSection id={SECTION_IDS.capabilities} step={step} title={strings.title} lead={strings.lead}>
      {loading && <p className="text-[13px] text-fg-subtle">{strings.loading}</p>}
      {failed && (
        <Note tone="warn" icon={<Info size={13} strokeWidth={2.1} />}>
          {strings.failed}
        </Note>
      )}

      {!loading && !failed && capabilities.length === 0 && (
        <div>
          <p className="text-[13px]">{strings.empty}</p>
          <p className="mt-1 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">{strings.emptyHint}</p>
        </div>
      )}

      {groups.map((group) => (
        <div key={group.system}>
          {/* A plain heading, not a labelled `<section>`: an inner region here would answer to
              the same `getByRole('region')` locators the six builder steps are found by. */}
          <div className="flex items-center gap-2.5 pb-2">
            <h3 className="eyebrow">{group.system}</h3>
            <span aria-hidden className="h-px flex-1 bg-rule" />
          </div>
          <ul className="space-y-2">
            {group.items.map((capability) => (
              <li key={capability.id}>
                <label
                  className={clsx(
                    'flex items-start gap-2.5 rounded-tile border px-3 py-2.5',
                    'transition-[background-color,border-color,box-shadow] duration-200',
                    granted.has(capability.id)
                      ? 'border-rule-strong bg-card-solid shadow-glass'
                      : 'border-rule bg-card-solid/55 hover:border-rule-strong',
                    disabled ? 'cursor-not-allowed opacity-45' : 'cursor-pointer'
                  )}
                >
                  <input
                    type="checkbox"
                    checked={granted.has(capability.id)}
                    disabled={disabled}
                    onChange={(event) => toggle(capability.id, event.target.checked)}
                    className="mt-[3px] h-[15px] w-[15px] shrink-0 cursor-pointer accent-primary"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-[13.5px] font-semibold tracking-[-0.014em]">{capability.name}</span>
                      <Badge tone={capability.wording === 'acts' ? 'alert' : 'neutral'}>
                        {capability.wording === 'acts' ? strings.acts : strings.reads}
                      </Badge>
                    </span>
                    <span className="mt-1 block text-pretty text-[12.5px] leading-[1.5] text-fg-muted">
                      {capability.description}
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      ))}

      <p className="text-[12.5px] text-fg-muted">{strings.selectedCount(i18n, draft.capabilityIds.length)}</p>
    </BuilderSection>
  )
}
